import React, { useEffect, useState, useCallback, useRef } from "react";
import { useLocation } from "react-router-dom";
import LinkAccount from "./LinkAccount";
import { getAccounts, getHoldings, getConnectionHealthSummary, syncTransactions } from "../../api/plaidClient";
import SummaryRow from "./SummaryRow";
import AccountGrid from "./AccountGrid";
import AccountDetail from "./AccountDetail";
import ConnectionHealthBanner from "../ui/ConnectionHealthBanner";
import SavingsRateCard from "./SavingsRateCard";
import SpendingBreakdownCard from "./SpendingBreakdownCard";
import FiNumberCard from "./FiNumberCard";
import OnboardingFlow from "../Onboarding/OnboardingFlow";
import { useAuth } from "../../contexts/AuthContext";

function SectionHeader({ title, subtitle }) {
    return (
        <div className="mb-4">
            <h2 className="text-lg font-semibold text-gray-900">{title}</h2>
            {subtitle && <p className="text-sm text-gray-500 mt-0.5">{subtitle}</p>}
        </div>
    );
}

export default function PortfolioPage() {
    const { user } = useAuth();
    const location = useLocation();
    const [accounts, setAccounts] = useState([]);
    const [holdings, setHoldings] = useState([]);
    const [healthSummary, setHealthSummary] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [selectedAccount, setSelectedAccount] = useState(null);
    const [financialPeriod, setFinancialPeriod] = useState(6);
    const [syncing, setSyncing] = useState(false);
    const abortControllerRef = useRef(null);

    useEffect(() => {
        setSelectedAccount(null);
    }, [location.key]);

    const fetchData = useCallback(async (awaitSync = false) => {
        if (abortControllerRef.current) {
            abortControllerRef.current.abort();
        }

        abortControllerRef.current = new AbortController();
        const signal = abortControllerRef.current.signal;

        setLoading(true);
        setError(null);

        try {
            if (awaitSync) {
                setSyncing(true);
                try {
                    await syncTransactions(signal);
                } catch {
                    // Sync failure is non-fatal
                } finally {
                    setSyncing(false);
                }
            } else {
                syncTransactions(signal).catch(() => {});
            }

            const [acctList, holdingsResp, healthResp] = await Promise.all([
                getAccounts(true, signal),
                getHoldings(signal),
                getConnectionHealthSummary(signal).catch(() => null),
            ]);

            setAccounts(acctList || []);
            setHoldings(holdingsResp || []);
            setHealthSummary(healthResp);
            setLoading(false);
        } catch (e) {
            if (e.name === 'AbortError' || e.name === 'CanceledError') {
                return;
            }
            console.error("Error fetching portfolio data:", e);
            setError("Could not load portfolio data.");
            setLoading(false);
        }
    }, []);

    const handleLinkSuccess = useCallback(() => {
        fetchData(true);
    }, [fetchData]);

    useEffect(() => {
        fetchData();

        return () => {
            if (abortControllerRef.current) {
                abortControllerRef.current.abort();
            }
        };
    }, [fetchData]);

    if (loading) {
        return (
            <div className="text-center py-12">
                <h1 className="text-2xl font-semibold text-gray-900 mb-4">Dashboard</h1>
                <div className="flex items-center justify-center gap-3">
                    <div className="animate-spin w-5 h-5 border-2 border-gray-200 border-t-gray-700 rounded-full" />
                    <span className="text-gray-500">Loading your dashboard...</span>
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="text-center py-12">
                <h1 className="text-2xl font-semibold text-gray-900 mb-4">Dashboard</h1>
                <div className="card max-w-md mx-auto p-6">
                    <p className="text-danger-500 mb-4">{error}</p>
                    <p className="text-gray-600 mb-4">Try refreshing or linking an account again.</p>
                    <button onClick={fetchData} className="btn-primary">
                        Try again
                    </button>
                </div>
            </div>
        );
    }

    if (accounts.length === 0) {
        return (
            <OnboardingFlow onComplete={() => fetchData()} />
        );
    }

    if (selectedAccount) {
        return (
            <AccountDetail
                account={selectedAccount}
                onBack={() => setSelectedAccount(null)}
            />
        );
    }

    const firstName = user?.displayName?.split(' ')[0];

    return (
        <div className="max-w-7xl mx-auto">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
                <div>
                    <h1 className="text-xl sm:text-2xl font-semibold text-gray-900 tracking-tight">
                        {firstName ? `${firstName}'s dashboard` : "Your dashboard"}
                    </h1>
                    <p className="text-gray-500 mt-0.5 text-sm">Your wealth at a glance</p>
                </div>
                <LinkAccount onLinkSuccess={handleLinkSuccess} />
            </div>

            <ConnectionHealthBanner healthSummary={healthSummary} />

            <SummaryRow />

            <div className="mt-10">
                <SectionHeader
                    title="Financial health"
                    subtitle="Your savings rate, spending patterns, and path to FI"
                />
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                    <SavingsRateCard months={financialPeriod} onMonthsChange={setFinancialPeriod} syncing={syncing} />
                    <FiNumberCard months={financialPeriod} syncing={syncing} />
                </div>
                <div className="mt-6">
                    <SpendingBreakdownCard months={financialPeriod} onMonthsChange={setFinancialPeriod} syncing={syncing} />
                </div>
            </div>

            <div className="mt-10">
                <SectionHeader
                    title="Accounts"
                    subtitle={`${accounts.length} linked account${accounts.length !== 1 ? "s" : ""}`}
                />
                <AccountGrid
                    accounts={accounts}
                    holdings={holdings}
                    onSelect={setSelectedAccount}
                />
            </div>
        </div>
    );
}
